import { FC } from 'react';
import { Users } from 'lucide-react';

interface CapacityIndicatorProps {
  booked: number;
  capacity: number;
  size?: 'xs' | 'sm' | 'md';
  showNumbers?: boolean;
  className?: string;
}

export const CapacityIndicator: FC<CapacityIndicatorProps> = ({
  booked,
  capacity,
  size = 'sm',
  showNumbers = true,
  className = ''
}) => {
  const available = capacity - booked;
  const availabilityRatio = available / capacity;
  
  // Get capacity-based color for available number only
  const getAvailableNumberColor = () => {
    if (availabilityRatio > 0.7) {
      return 'text-green-600';
    } else if (availabilityRatio > 0.3) {
      return 'text-yellow-600';
    } else {
      return 'text-red-600';
    }
  };

  const availableColor = getAvailableNumberColor();
  
  // Size configurations - optimized for better spacing
  const sizeConfig = {
    xs: {
      text: 'text-xs',
      icon: 'w-2.5 h-2.5',
      padding: 'px-2 py-1',
      dot: 'w-1.5 h-1.5'
    },
    sm: {
      text: 'text-xs',
      icon: 'w-3 h-3',
      padding: 'px-2.5 py-1',
      dot: 'w-2 h-2'
    },
    md: {
      text: 'text-sm',
      icon: 'w-3.5 h-3.5',
      padding: 'px-3 py-1.5',
      dot: 'w-2.5 h-2.5'
    }
  };

  const config = sizeConfig[size];

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {/* Capacity numbers in emerald-styled box matching custom adventures */}
      {showNumbers && (
        <div className={`
          inline-flex items-center gap-0.5
          ${config.padding} 
          bg-emerald-700/30 text-emerald-100 
          border border-emerald-600/50 
          rounded text-xs font-medium
        `}>
          {/* Users icon (restored) */}
          <Users className={`${config.icon} text-emerald-100 flex-shrink-0`} />
          
          {/* Available number with color-coding, capacity in emerald */}
          <span className={`${config.text} font-medium tabular-nums ${availableColor}`}>
            {available}
          </span>
          <span className={`${config.text} text-emerald-200`}>/</span>
          <span className={`${config.text} font-medium tabular-nums text-emerald-100`}>
            {capacity}
          </span>
        </div>
      )}
    </div>
  );
};
